import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import {
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "../pages/Boards/api/boardApi";
import useUserWs from "../pages/Boards/useUserWs";
import { getSession } from "../utils/session";
import "./NotificationsBell.scss";

const POLL_MS = 30_000;

// Bell + dropdown panel for in-app notifications. Lives in AdminLayout
// topbar. Polls every 30s; on the board page, BoardWs pushes
// `notification.added` events that we patch in optimistically (see the
// global handler installed in useEffect — listens for a synthetic event
// dispatched by useBoardWs callers).
export default function NotificationsBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const meId = getSession()?._id;
  const ref = useRef(null);

  const refresh = async () => {
    try {
      const res = await listNotifications({ limit: 50 });
      setItems(res.data || []);
      setUnread(res.unreadCount || 0);
    } catch {
      // Silent — bell is best-effort.
    }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, POLL_MS);

    // Legacy: useBoardWs in the board page rebroadcasts notification.added
    // via window event — kept for backward-compat. Per-user WS (below) is
    // the primary live channel now.
    const onLive = (e) => {
      const n = e.detail;
      if (!n || String(n.user_id) !== String(meId)) return;
      ingestLiveNotification(n);
    };
    window.addEventListener("smarthatch:notification", onLive);

    const onDoc = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);

    return () => {
      clearInterval(t);
      window.removeEventListener("smarthatch:notification", onLive);
      document.removeEventListener("mousedown", onDoc);
    };
  }, [meId]);

  const ingestLiveNotification = (n) => {
    setItems((arr) => [{
      _id: n.notification_id,
      user_id: n.user_id,
      actor: n.actor,
      kind: n.kind,
      board_id: n.board_id,
      item_id: n.item_id,
      update_id: n.update_id,
      body: n.body,
      read: false,
      createdAt: n.createdAt,
    }, ...arr.filter((x) => x._id !== n.notification_id)]);
    setUnread((u) => u + 1);
  };

  // Per-user WebSocket channel — primary live feed. Receives notifications
  // for the current user no matter which board (or no board) they're on.
  useUserWs(meId, (event, payload) => {
    if (event === "notification.added" && payload?.user_id) {
      ingestLiveNotification(payload);
    }
  });

  const handleClick = async (n) => {
    setOpen(false);
    if (!n.read) {
      try {
        await markNotificationRead(n._id);
        setItems((arr) => arr.map((x) => (x._id === n._id ? { ...x, read: true } : x)));
        setUnread((u) => Math.max(0, u - 1));
      } catch { /* */ }
    }
    if (n.board_id) {
      navigate(`/boards/${n.board_id}`);
    }
  };

  const handleMarkAll = async () => {
    try {
      await markAllNotificationsRead();
      setItems((arr) => arr.map((x) => ({ ...x, read: true })));
      setUnread(0);
    } catch { /* */ }
  };

  return (
    <div className="notif-bell" ref={ref}>
      <button
        type="button"
        className="notif-bell-btn"
        onClick={() => setOpen((o) => !o)}
        title="Notifications"
      >
        <span className="notif-icon"><Bell size={18} /></span>
        {unread > 0 && <span className="notif-badge">{unread > 99 ? "99+" : unread}</span>}
      </button>
      {open && (
        <div className="notif-panel">
          <header className="notif-panel-head">
            <span>Notifications</span>
            {unread > 0 && (
              <button className="notif-link" onClick={handleMarkAll}>Mark all read</button>
            )}
          </header>
          {items.length === 0 && (
            <div className="notif-empty">No notifications yet.</div>
          )}
          <div className="notif-list">
            {items.map((n) => (
              <button
                key={n._id}
                className={`notif-row ${n.read ? "" : "unread"}`}
                onClick={() => handleClick(n)}
              >
                <div className="notif-row-body">{n.body || describeKind(n)}</div>
                <div className="notif-row-meta">
                  <span className="notif-kind">{n.kind}</span>
                  <span className="notif-ts">{formatTs(n.createdAt)}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function describeKind(n) {
  if (n.kind === "mention") return "Someone mentioned you in an update";
  if (n.kind === "assigned") return "You were assigned to an item";
  if (n.kind === "status_changed") return "Status changed on an item you follow";
  if (n.kind === "automation") return "An automation ran on an item you follow";
  return n.kind;
}

function formatTs(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = Date.now();
  const diff = (now - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
